import { useState } from 'react';
import { Globe, RefreshCw, Shield } from 'lucide-react';
import { StatusDot } from '../common/StatusDot';
import api from '../../services/api';

interface LocalAccessTabProps {
  siteId: string;
  localUrl?: string;
}

interface ConnectivityResult {
  reachable: boolean;
  latency_ms: number;
  checked_at: string;
  local_url: string;
}

export function LocalAccessTab({ siteId, localUrl }: LocalAccessTabProps) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<ConnectivityResult | null>(null);

  const checkConnectivity = async () => {
    setChecking(true);
    try {
      const res = await api.get(`/v1/sites/${siteId}/connectivity`);
      setResult(res.data.data);
    } catch {
      setResult(null);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="max-w-lg space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-djin-blue" />
          <h3 className="font-semibold text-text-primary">Local EMS Access</h3>
        </div>

        {localUrl ? (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-text-muted mb-1">Local URL</p>
              <a href={localUrl} target="_blank" rel="noopener noreferrer" className="text-djin-blue hover:underline text-sm font-mono">
                {localUrl}
              </a>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={checkConnectivity}
                disabled={checking}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-djin-blue text-white text-sm font-medium hover:bg-djin-blue-dark disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
                Check Connectivity
              </button>

              {result && (
                <div className="flex items-center gap-2">
                  <StatusDot status={result.reachable ? 'online' : 'offline'} />
                  <span className={`text-sm font-medium ${result.reachable ? 'text-status-online' : 'text-status-offline'}`}>
                    {result.reachable ? `Reachable (${result.latency_ms}ms)` : 'Unreachable'}
                  </span>
                </div>
              )}
            </div>

            {result?.checked_at && (
              <p className="text-xs text-text-muted">Last checked: {new Date(result.checked_at).toLocaleString()}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-text-muted">No local URL configured for this site.</p>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">VPN Required</p>
          <p className="text-xs text-amber-700 mt-1">
            Access to the local EMS dashboard requires an active VPN connection to the site network.
            Contact your administrator for VPN credentials.
          </p>
        </div>
      </div>
    </div>
  );
}
